import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

interface MfisScoreField {
  controlName: string;
  maxScore: number;
  items: number[];
}

const MFIS_ANSWER_POINTS = new Map<string, number>([
  ['never', 0],
  ['rarely', 1],
  ['sometimes', 2],
  ['often', 3],
  ['almost always', 4],
]);

// Item n of the form is question(653 + n), so item 1 is question654.
const MFIS_ITEM_ID_OFFSET = 653;

const MFIS_SUB_SCORES: MfisScoreField[] = [
  // Physical
  {
    controlName: 'question675',
    maxScore: 36,
    items: [4, 6, 7, 10, 13, 14, 17, 20, 21],
  },
  // Cognitive
  {
    controlName: 'question676',
    maxScore: 40,
    items: [1, 2, 3, 5, 11, 12, 15, 16, 18, 19],
  },
  // Psychosocial
  { controlName: 'question677', maxScore: 8, items: [8, 9] },
];

const MFIS_TOTAL_CONTROL_NAME = 'question678';
const MFIS_TOTAL_MAX_SCORE = 84;

@Component({
  selector: 'app-mfis-questions',
  templateUrl: './mfis-questions.component.html',
  styleUrl: './mfis-questions.component.css',
  standalone: false,
})
export class MfisQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateScores();
    });
    this.recalculateScores();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any, event: MouseEvent): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control || control.disabled) return;

    if (control.value === value) {
      // Otherwise nz-radio selects the same answer again right after this.
      event.preventDefault();
      event.stopImmediatePropagation();
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split('||')[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split('||')[1]?.trim() ?? '';
  }

  getItemTitle(element: IQuestionnaireQuestion): string {
    const title = this.getQuestionTitle(element.title);
    return /^\d+[.)]/.test(title)
      ? title
      : `${element.id - MFIS_ITEM_ID_OFFSET}. ${title}`;
  }

  getChoicePoints(choiceText: string): string {
    const points = this.getPoints(choiceText);
    return points === null ? '' : `(${points})`;
  }

  // Shows the answers from 0 to 4 points in every row, whatever the template order
  getOrderedChoices(element: IQuestionnaireQuestion): IQuestionnaireChoice[] {
    const rank = (choice: IQuestionnaireChoice) =>
      this.getPoints(choice.text) ?? MFIS_ANSWER_POINTS.size;

    return [...(element.choices ?? [])].sort((a, b) => rank(a) - rank(b));
  }

  isScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.type === 'text' && this.getMaxScore(element.name) !== null;
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.name === MFIS_TOTAL_CONTROL_NAME;
  }

  // The template has no maximums, so the UI supplies them.
  getMaxScore(controlName: string): number | null {
    if (controlName === MFIS_TOTAL_CONTROL_NAME) {
      return MFIS_TOTAL_MAX_SCORE;
    }

    return (
      MFIS_SUB_SCORES.find((subScore) => subScore.controlName === controlName)
        ?.maxScore ?? null
    );
  }

  getScoreDisplay(controlName: string): string {
    const value = this.formGroup.get(controlName)?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  private recalculateScores(): void {
    const subScores = MFIS_SUB_SCORES.map((subScore) => {
      const score = this.calculateSubScore(subScore);
      this.setLockedScore(subScore.controlName, score);
      return score;
    });

    // Live total of the sub scales answered so far.
    const answeredSubScores = subScores.filter(
      (score): score is number => score !== null,
    );
    const total = answeredSubScores.length
      ? answeredSubScores.reduce((sum, score) => sum + score, 0)
      : null;
    this.setLockedScore(MFIS_TOTAL_CONTROL_NAME, total);

    this.cdr.detectChanges();
  }

  private calculateSubScore(subScore: MfisScoreField): number | null {
    const points = subScore.items
      .map((item) =>
        this.getSelectedPoints(`question${MFIS_ITEM_ID_OFFSET + item}`),
      )
      .filter((point): point is number => point !== null);

    return points.length ? points.reduce((sum, point) => sum + point, 0) : null;
  }

  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.elements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);

    return this.getPoints(choice?.text);
  }

  // Normalised like the backend: trimmed, lower case and without accents.
  private getPoints(text?: string): number | null {
    const word = (text ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');

    return MFIS_ANSWER_POINTS.get(word) ?? null;
  }

  private setLockedScore(controlName: string, score: number | null): void {
    const control = this.formGroup.get(controlName);
    if (!control) return;

    control.setValue(score === null ? '' : String(score), {
      emitEvent: false,
    });
    control.disable({ emitEvent: false });
  }
}
